/**
 * Frontend extraction types — mirrors backend extraction types in camelCase.
 */

import type { SourceHighlightData } from '@/stores/assistant-store'

export type ExtractionCategory =
  | 'evaluation_criteria'
  | 'compliance_matrix'
  | 'submission_checklist'
  | 'key_dates'
  | 'risk_register'

/**
 * Categories the extraction-stream Function URL accepts. Superset of the
 * item-producing `ExtractionCategory`. `tender_overview` streams progress
 * phases only (no `extraction:item` events) — its result is re-fetched via
 * REST on completion.
 */
export type AnalysisCategory = ExtractionCategory | 'tender_overview'

/**
 * Every value that can appear in `extraction_runs.category`. Mirrors the
 * backend `RunCategory` exactly.
 *
 * `compliance_assessment` is a pseudo-category: it produces no extracted items,
 * it re-scores existing compliance rows. It rides the runs table purely for
 * lifecycle tracking (running → completed/failed), which is what lets the
 * frontend poll an assessment the same way it polls an extraction.
 */
export type RunCategory = AnalysisCategory | 'compliance_assessment'

export type ExtractionState = 'empty' | 'extracting' | 'populated'

/**
 * Coarse pipeline phases reported by the backend during extraction. Used to
 * drive the progress label shown in the extracting-state UI. Synthesis is
 * the only phase that lasts minutes — backend ticks it repeatedly while the
 * LLM streams tokens.
 */
export type ExtractionPhase =
  | 'discovering'
  | 'ranking'
  | 'synthesizing'
  | 'verifying'
  | 'saving'

/**
 * Status of a single extraction run, as persisted in the `extraction_runs`
 * table. Returned by `GET /bids/{id}/extraction-runs`.
 */
export type ExtractionRunStatus = 'pending' | 'running' | 'completed' | 'failed'

/**
 * How far through its window plan a running async sweep is. Only a running
 * sweep run carries this — an inline run has no windows.
 */
export interface SweepProgress {
  done: number
  total: number
}

/**
 * Run summary returned by `GET /bids/{id}/extraction-runs`. Used by the
 * frontend to rehydrate the in-progress stepper after a page refresh, and as
 * the ONLY status surface the async sweep watcher polls.
 */
export interface ExtractionRunSummary {
  id: string
  bidId: string
  /**
   * Widened past `ExtractionCategory` because the endpoint returns the bid's
   * runs unfiltered — `tender_overview` and `compliance_assessment` rows land
   * in the same array.
   */
  category: RunCategory
  status: ExtractionRunStatus
  phase: ExtractionPhase | null
  itemCount: number
  errorMessage: string | null
  createdAt: string
  completedAt: string | null
  /**
   * Async sweep worker liveness. Null on inline (non-sweep) runs — which is
   * exactly how the client tells the two apart: a running run with a heartbeat
   * belongs to the sweep watcher, one without belongs to inline recovery.
   */
  heartbeatAt?: string | null
  /** Window progress, present only while a sweep run is running. */
  sweepProgress?: SweepProgress | null
  /** Final sweep coverage stats. Null on runs that never swept. */
  coverage?: Record<string, number> | null
  /**
   * The run completed from a read known to be incomplete — the AI's answer was
   * cut off or broke — so it saved what it read and kept the existing rows it
   * did not reach. Absent from older backends.
   */
  partialRead?: boolean
}

// Per-category extracted item types (camelCase for frontend)

export interface ExtractedCriterion {
  id?: string
  title: string
  description: string
  weightPercentage: number | null
  weightPoints: number | null
  note?: string | null
  sourceQuote: string
  sourceDocument: string
  sourcePage: number
  sourceDocumentId?: string | null
  sourceHighlightData?: SourceHighlightData | null
  subCriteria: ExtractedCriterion[]
  extractionSource?: 'ai' | 'manual'
}

/** What the response pack owes a compliance item — only 'narrative' counts toward coverage. */
export type ResponseExpectation = 'narrative' | 'evidence' | 'form' | 'post_award'

/**
 * Where a requirement currently sits against the organisation's evidence.
 * `unassessed` is the resting state — no AI verdict yet, and never a claim.
 */
export type PositionValue =
  | 'unassessed'
  | 'comply'
  | 'partial'
  | 'non_comply'
  | 'not_applicable'

/** How binding the requirement is. Note `information`, NOT `informational`. */
export type CriticalityValue = 'mandatory' | 'desirable' | 'information'

/** Confidence the assessor attaches to a position verdict. */
export type PositionConfidence = 'high' | 'medium' | 'low'

/**
 * Why the assessor left a requirement at `unassessed` (mig 180), when it knows.
 * `not_searched`: it gave the requirement no library search by design
 * (pricing, declarations, contract terms), so the bid manager has to confirm
 * it themselves — shown as "Needs your confirmation" rather than "Needs review".
 */
export type UnassessedReason = 'not_searched'

/**
 * Evidence behind a position verdict. Library citations point at a chunk of a
 * real document (and carry highlight geometry when we have it); profile
 * citations point at a field of the org profile, which has no document.
 */
export type PositionCitation =
  | {
      source: 'library'
      documentId: string
      documentName: string
      page: number | null
      quote: string
      chunkIndex: number
      highlightData: SourceHighlightData | null
    }
  | { source: 'profile'; profileField: string; detail: string }

export interface ExtractedComplianceItem {
  id?: string
  title: string
  description: string
  category: ComplianceCategory
  refClause: string | null
  evidence: string | null
  note?: string | null
  status: string
  ownerId: string | null
  sourceQuote: string
  sourceDocument: string
  sourcePage: number
  sourceDocumentId?: string | null
  sourceHighlightData?: SourceHighlightData | null
  extractionSource?: 'ai' | 'manual'
  responseExpectation?: ResponseExpectation
  responseExpectationSource?: 'ai' | 'manual'
  /** Progress provenance (mig 168): 'derived' rows show coverage-derived Progress. */
  statusSource?: 'derived' | 'manual'

  // Compliance loop (position + criticality). `*Source` is stamped by the
  // server — 'manual' pins the value and takes it out of AI reassessment.
  criticality?: CriticalityValue
  criticalitySource?: 'ai' | 'manual'
  position?: PositionValue
  positionSource?: 'ai' | 'manual'
  positionRationale?: string | null
  positionConfidence?: PositionConfidence | null
  positionCitations?: PositionCitation[] | null
  positionAssessedAt?: string | null
  /**
   * The verdict the AI itself last asserted (mig 156), post-downgrade. `position`
   * may have been overridden since; this does not move — so agreed ⟺
   * `position === aiPosition`, overridden ⟺ they differ. `null` means the AI has
   * never assessed this row (including rows assessed before mig 156), which is
   * NOT the same as `'unassessed'`.
   */
  aiPosition?: PositionValue | null
  /**
   * Why the AI's last verdict was `unassessed` (mig 180). `null`/absent for
   * rows assessed before mig 180, rows the AI searched for, and any row whose AI
   * verdict was something else. Only read while the row is still `unassessed`
   * with an assessed stamp — see `positionDisplayState`.
   */
  unassessedReason?: UnassessedReason | null
  /**
   * The bid's stable requirement number (mig 194) — shown as `REQ-<n>`. The
   * server hands it out the first time a row is active, so it is `null` while
   * a row is awaiting review and never changes afterwards. Absent from older
   * responses.
   */
  displayNumber?: number | null
}

export interface ExtractedSubmissionItem {
  id?: string
  title: string
  description: string
  format: string | null
  limit: string | null
  copies: number | null
  method: string | null
  status: string
  ownerId: string | null
  note?: string | null
  linkedArtifactId?: string | null
  linkedSectionKey?: string | null
  linkedArtifactName?: string | null
  linkedSectionTitle?: string | null
  sourceQuote: string
  sourceDocument: string
  sourcePage: number
  sourceDocumentId?: string | null
  sourceHighlightData?: SourceHighlightData | null
  extractionSource?: 'ai' | 'manual'
  /** Layer-1 triage class (structure proposals): rendered as Type pills */
  triageClass?: 'response' | 'attachment' | 'form' | 'acknowledge' | null
}

export type KeyDatePhase =
  | 'pre_tender'
  | 'tender'
  | 'post_award'
  | 'construction'
  | 'contractual'

export interface ExtractedDate {
  id?: string
  event: string
  description: string | null
  dateIso: string | null
  dateText: string
  time: string | null
  isSubmissionDeadline: boolean
  phase: KeyDatePhase
  note?: string | null
  sourceQuote: string
  sourceDocument: string
  sourcePage: number
  sourceDocumentId?: string | null
  sourceHighlightData?: SourceHighlightData | null
  extractionSource?: 'ai' | 'manual'
  /**
   * True once a person has edited this row's date/time by hand. The API already
   * returns it; the UI uses it to mark the value as deliberately set rather
   * than AI-extracted, so a re-run's disagreement reads as a change, not a fix.
   */
  userTouched?: boolean
}

export type RiskCategory =
  | 'Commercial'
  | 'Legal & Liability'
  | 'Scope & Specification'
  | 'Delivery & Timeline'
  | 'Insurance & Financial'
  | 'Regulatory & Environmental'

export type RiskSeverity = 'critical' | 'high' | 'medium' | 'low'

export type RiskImpactArea = 'financial' | 'schedule' | 'legal' | 'reputation' | 'safety'

/**
 * What the bid team decided to do about a risk.
 *
 * Frontend copy of the backend union in
 * `backend/src/repositories/risk-register-items.ts` — change one, change both.
 */
export type RiskDecision = 'accept' | 'price' | 'negotiate' | 'clarify' | 'walk_away'

export interface ExtractedRiskItem {
  id?: string
  title: string
  description: string
  category: RiskCategory
  severity: RiskSeverity
  impactArea: RiskImpactArea
  contractClause: string | null
  mitigation: string | null
  mitigationPlan?: string | null
  residualSeverity?: RiskSeverity | null
  riskDecision?: RiskDecision | null
  note?: string | null
  status: string
  ownerId: string | null
  sourceQuote: string
  sourceDocument: string
  sourcePage: number
  sourceDocumentId?: string | null
  sourceHighlightData?: SourceHighlightData | null
  extractionSource?: 'ai' | 'manual'
}

/**
 * Compliance-matrix categories, cut by who on the bid team owns the item.
 *
 * Frontend copy of the backend list (separate package, no shared module).
 * `src/types/extraction.test.ts` pins it equal to
 * `backend/src/lib/extraction/types.ts` — change one, change both.
 */
export const COMPLIANCE_CATEGORIES = [
  'Conforming',
  'Insurance',
  'Financial',
  'Licensing & Accreditation',
  'Management Systems',
  'Contract Terms',
  'Policy & Social Procurement',
  'Legal & Statutory',
  'Pricing',
  'Capability & Personnel',
  'Other',
] as const

export type ComplianceCategory = (typeof COMPLIANCE_CATEGORIES)[number]

export type ExtractedItem =
  | ExtractedCriterion
  | ExtractedComplianceItem
  | ExtractedSubmissionItem
  | ExtractedDate
  | ExtractedRiskItem

/** Category state tracked in the extraction store */
export interface CategoryState {
  state: ExtractionState
  items: ExtractedItem[]
  streamingItems: ExtractedItem[]
  itemCount: number
  lastExtractedAt: string | null
  /**
   * The Find-new-items review list. Since mig 194 these rows are saved
   * `awaiting_review` and the server returns them as `awaitingItems` on the
   * `?includeRemoved=true` GET, so the list survives a reload. They are never
   * part of `items` (the table) until accepted.
   */
  newItems: ExtractedItem[]
  /**
   * Superseded rows from the most recent merge (PR 2). A re-run keeps prior
   * user-touched items alive but supersedes stale AI rows; these land here so
   * the UI can offer a "review pile" (restore / confirm-remove) instead of
   * silently dropping them. Populated from the `?includeRemoved=true` GET.
   */
  removedItems: ExtractedItem[]
  /**
   * Match-texts (title, or `event` for key_dates) of dismissed tombstones.
   * Lightweight projection from the GET — Find-new-items dedups against these
   * so a dismissed suggestion never resurfaces.
   */
  dismissedTexts: string[]
  isReviewingNewItems: boolean
  /** True when AI extraction recently completed and the user hasn't yet viewed the sub-tab. */
  hasUnseenCompletion: boolean
  /**
   * True while the frontend is polling the REST API for items after a stream failure.
   * The Lambda continues running and persisting even when the client stream drops,
   * so polling catches up once the backend finishes.
   */
  isRecovering: boolean
  /**
   * Set when the stream failed and background recovery polling has given up (or the
   * stream has not yet been retried). Surfaces a banner + retry affordance in the UI.
   */
  streamError: string | null
  /**
   * Latest pipeline phase reported by the backend during the current extraction.
   * Drives the progress label in the extracting-state UI. Null when no extraction
   * has reported a phase yet (very early in a run, or pre-fix runs).
   */
  phase: ExtractionPhase | null
  /**
   * Windows read / windows planned for the async sweep currently being watched
   * for this category. Null whenever no sweep is in flight (including inline
   * runs, which have no window plan).
   */
  sweepProgress: SweepProgress | null
  /**
   * True from the moment this category is handed to the async sweep watcher
   * until the run reaches a terminal state. Deliberately SEPARATE from
   * `sweepProgress`: the "you can leave this page" promise is true for the
   * whole sweep, including before the first window count arrives and on any
   * cycle where the server can't read progress. Gating that copy on the
   * counter would blink it off exactly when the run is least legible.
   */
  isSweeping: boolean
  /**
   * The latest run completed from a partial read: it could not read the AI's
   * whole answer, so the rows it did not reach were kept rather than removed.
   * Shows `PARTIAL_READ_NOTICE` over the table until the next run starts.
   */
  partialRead: boolean
}

/**
 * Shown over a category's table after a partial read. One constant so the copy
 * can change in one place — wording pending product sign-off (2026-10-02).
 */
export const PARTIAL_READ_NOTICE =
  "Couldn't read the full result, so your existing items were kept. Run again to refresh."

/** Human-readable labels for extraction categories */
export const EXTRACTION_CATEGORY_LABELS: Record<ExtractionCategory, string> = {
  evaluation_criteria: 'Evaluation Criteria',
  compliance_matrix: 'Compliance Matrix',
  submission_checklist: 'Submission Checklist',
  key_dates: 'Key Dates',
  risk_register: 'Risk Register',
}

/** Key date phase labels */
export const KEY_DATE_PHASE_LABELS: Record<KeyDatePhase, string> = {
  pre_tender: 'Pre-Tender',
  tender: 'Tender',
  post_award: 'Post-Award',
  construction: 'Construction',
  contractual: 'Contractual',
}

/** Key date phase badge colors */
export const KEY_DATE_PHASE_COLORS: Record<KeyDatePhase, string> = {
  pre_tender: 'bg-blue-100 text-blue-800',
  tender: 'bg-amber-100 text-amber-800',
  post_award: 'bg-green-100 text-green-800',
  construction: 'bg-purple-100 text-purple-800',
  contractual: 'bg-gray-100 text-gray-600',
}

/** Compliance category badge colors */
export const COMPLIANCE_CATEGORY_COLORS: Record<ComplianceCategory, string> = {
  Conforming: 'bg-gray-100 text-gray-800',
  Insurance: 'bg-blue-100 text-blue-800',
  Financial: 'bg-green-100 text-green-800',
  'Licensing & Accreditation': 'bg-orange-100 text-orange-800',
  'Management Systems': 'bg-purple-100 text-purple-800',
  'Contract Terms': 'bg-cyan-100 text-cyan-800',
  'Policy & Social Procurement': 'bg-indigo-100 text-indigo-800',
  // Inherits the retired 'Legal & Regulatory' hue: it is the closest successor.
  'Legal & Statutory': 'bg-red-100 text-red-800',
  Pricing: 'bg-yellow-100 text-yellow-800',
  // Inherits the retired 'Experience & Capability' hue.
  'Capability & Personnel': 'bg-amber-100 text-amber-800',
  // Deliberately the faintest label in the set: 'Other' means unclassified,
  // and a coloured chip would imply it names something.
  Other: 'bg-gray-100 text-gray-600',
}

/** Risk register category badge colors */
export const RISK_CATEGORY_COLORS: Record<RiskCategory, string> = {
  'Commercial': 'bg-green-100 text-green-800',
  'Legal & Liability': 'bg-red-100 text-red-800',
  'Scope & Specification': 'bg-amber-100 text-amber-800',
  'Delivery & Timeline': 'bg-blue-100 text-blue-800',
  'Insurance & Financial': 'bg-purple-100 text-purple-800',
  'Regulatory & Environmental': 'bg-teal-100 text-teal-800',
}

/**
 * Category dots for the Risk column — one hue per key, matching the badge above.
 *
 * The category badge left the default column set with the row diet, but the
 * category is how a bid manager scans a register, so it rides along as a dot on
 * the title and is named on hover. Solid `-500` because a 2px dot at the badge's
 * `-100` wash is invisible; the hue is the badge's, so the two never disagree.
 */
/** Risk severity badge colors */
export const RISK_SEVERITY_COLORS: Record<RiskSeverity, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-green-100 text-green-800',
}

/** Risk severity labels */
export const RISK_SEVERITY_LABELS: Record<RiskSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

/** Risk decision labels — the bid team's call on each risk */
export const RISK_DECISION_LABELS: Record<RiskDecision, string> = {
  accept: 'Accept',
  price: 'Price in',
  negotiate: 'Raise departure',
  clarify: 'Raise clarification',
  walk_away: 'Walk away',
}

/** Display order for the risk decision picker */
export const ALL_RISK_DECISIONS: RiskDecision[] = ['accept', 'price', 'negotiate', 'clarify', 'walk_away']

/** Risk impact area labels */
export const RISK_IMPACT_AREA_LABELS: Record<RiskImpactArea, string> = {
  financial: 'Financial',
  schedule: 'Schedule',
  legal: 'Legal',
  reputation: 'Reputation',
  safety: 'Safety',
}

/** All risk categories for filter dropdowns */
export const ALL_RISK_CATEGORIES: RiskCategory[] = [
  'Commercial', 'Legal & Liability', 'Scope & Specification',
  'Delivery & Timeline', 'Insurance & Financial', 'Regulatory & Environmental',
]

/** Generic phase labels (apply to every category) */
const GENERIC_PHASE_LABELS: Record<Exclude<ExtractionPhase, 'synthesizing'>, string> = {
  discovering: 'Reading tender documents…',
  ranking: 'Ranking relevant sections…',
  verifying: 'Verifying citations…',
  saving: 'Saving results…',
}

/** Category-specific labels for the long synthesis phase */
const SYNTHESIZING_LABELS: Record<AnalysisCategory, string> = {
  evaluation_criteria: 'Identifying evaluation criteria…',
  compliance_matrix: 'Identifying compliance requirements…',
  submission_checklist: 'Building submission checklist…',
  key_dates: 'Extracting key dates…',
  risk_register: 'Identifying risks…',
  tender_overview: 'Writing summary…',
}

/**
 * Resolve the user-facing progress label for a given category + phase.
 * Falls back to a generic label when the phase isn't reported yet (e.g.
 * very early in a run, or a backend version that doesn't emit progress).
 *
 * Accepts the wider `AnalysisCategory` so `tender_overview` — which streams
 * the same five phases but produces no items — can share the phase stepper.
 */
export function getExtractionPhaseLabel(
  category: AnalysisCategory,
  phase: ExtractionPhase | null
): string {
  if (phase === null) return 'Analysing tender documents…'
  if (phase === 'synthesizing') return SYNTHESIZING_LABELS[category]
  return GENERIC_PHASE_LABELS[phase]
}
